import * as React from "react";
import { Table, Button, Dropdown, Menu, Modal, Tooltip, message } from "antd";
import { MoreOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { Pagination } from "antd";
import { toasty } from "../../../../../Helper";
import { useState } from "react";

const DataTable = ({
	rows,
	page,
	totalItems,
	handleChangePage,
	handleEditRow,
	handleDeleteRow,
	handleSelectChange,
	roleDetails,
}) => {
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [menuIndex, setMenuIndex] = React.useState(null);
	const navigate = useNavigate();
	const [openDialog, setOpenDialog] = React.useState(false);
	const [selectedRowId, setSelectedRowId] = React.useState(null);
	const [idTooltip, setIdTooltip] = React.useState("Copy ID");

	// Define a default page size if not provided
	const DEFAULT_PAGE_SIZE = 15;
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

	const handleMenuOpen = (event, index) => {
		setAnchorEl(event.currentTarget);
		setMenuIndex(index);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuIndex(null);
	};

	const isMenuOpen = Boolean(anchorEl);

	const handleCopyId = (e, id) => {
		e.stopPropagation();
		navigator.clipboard.writeText(id).then(() => {
			setIdTooltip("Copied!");
			setTimeout(() => setIdTooltip("Copy ID"), 1500);
		});
		toasty("ID copied to clipboard!", "success");
	};

	const handleDeleteClick = (id) => {
		setSelectedRowId(id);
		setOpenDialog(true);
	};

	const handleDialogClose = () => {
		setOpenDialog(false);
		setSelectedRowId(null);
	};

	const handleConfirmDelete = () => {
		handleDeleteRow(selectedRowId);
		handleDialogClose();
	};

	const handleRowClick = (record, e) => {
		if (record && record.id) {
			roleDetails(record.id, e);
		}
	};

	const handlePageChange = (newPage) => {
		if (typeof newPage === 'number' && newPage >= 1) {
			handleChangePage(newPage, pageSize);  // Pass both page and pageSize
		}
	};

	const columns = [
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>ID</span>,
			dataIndex: 'id',
			render: (text) => (
				<>
					{text.slice(-10)}
					<Tooltip title={idTooltip}>
						<Button
							icon={<CopyOutlined />}
							size="small"
							onClick={(e) => handleCopyId(e, text)}
							style={{ marginLeft: 8 }}
						/>
					</Tooltip>
				</>
			),
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Created At</span>,
			dataIndex: 'created_at',
			render: (text) => {
				if (!text) return "N/A";
				const date = new Date(text);
				date.setHours(date.getHours() + 5);
				date.setMinutes(date.getMinutes() + 30);
				const day = String(date.getDate()).padStart(2, '0');
 	            const month = String(date.getMonth() + 1).padStart(2, '0'); // +1 because months are 0-based
 	           const year = date.getFullYear();
 
 	            return `${day}/${month}/${year}`;
 			},
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Organization</span>,
			dataIndex: 'organization_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Department</span>,
			dataIndex: 'department_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Sub-Department</span>,
			dataIndex: 'subdepartment_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Role Name</span>,
			dataIndex: 'role_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Salary Range</span>,
			dataIndex: 'salary_min',
			render: (_, record) => `${record.salary_min || 0} - ${record.salary_max || 0}`,
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Level</span>,
			dataIndex: 'level',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Work Type</span>,
			dataIndex: 'employment_type',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
			key: 'actions',
			render: (_, record, index) => (
				<Dropdown
					overlay={
						<Menu>
							<Menu.Item 
								key="edit" 
								onClick={() => handleEditRow(record.id)}
							>
								Edit
							</Menu.Item>
							<Menu.Item 
								key="delete" 
								onClick={() => handleDeleteClick(record.id)}
							>
								Delete
							</Menu.Item>
						</Menu>
					}
					trigger={["click"]}
				>
					<Button 
						icon={<MoreOutlined />} 
						onClick={(e) => e.stopPropagation()}
					/>
				</Dropdown>
			),
		},
	];

	// Ensure rows is an array
	const safeRows = Array.isArray(rows) ? rows : [];

	return (
		<>
			<Table
				columns={columns}
				dataSource={safeRows}
				rowKey="id"
				size="small"
				pagination={false}
				rowClassName="custom-row"
				scroll={{ x: 2000, y: "60vh" }}	
				className="antTableClass"
				onRow={(record) => ({
					onClick: (e) => {
						if (!e.target.closest('.ant-dropdown') && 
							!e.target.closest('.ant-dropdown-trigger') && 
							!e.target.closest('.ant-btn')) {
							handleRowClick(record, e);
						}
					},
					style: { cursor: 'pointer' }
				})}
			/>

			<div style={{ 
				display: 'flex', 
				justifyContent: 'space-between', 
				alignItems: 'center',
				padding: '16px 8px',
				background: 'rgba(238, 238, 240, 1)',
				borderTop: '0.5px solid rgba(218, 218, 221, 1)',
				borderRadius: '0 0 8px 8px',
				marginTop: '-10px'
			}}>
				<div style={{ 
					fontFamily: 'Inter', 
					fontSize: '12px', 
					fontWeight: 400, 
					lineHeight: '18px', 
					letterSpacing: '-0.1px', 
					color: '#27272A' 
				}}>
					Showing {Math.min(safeRows.length, pageSize)} of {totalItems} Roles
				</div>

				<Pagination
					current={page}
					total={totalItems}
					pageSize={pageSize}
					showSizeChanger={false}
					size="small"
					style={{ margin: '0 auto' }}
					onChange={handlePageChange}
					itemRender={(_, type, originalElement) => {
						if (type === 'prev' || type === 'next') {
							return null;
						}
						return originalElement;
					}}
					className="custom-pagination"
				/>

				<div style={{ display: 'flex', alignItems: 'center' }}>
					<button
						onClick={() => {
							if (page > 1) {
								handlePageChange(page - 1);
							}
						}}
						disabled={page <= 1}
						style={{ 
							background: 'none',
							border: 'none',
							fontFamily: 'Inter', 
							fontSize: '12px', 
							fontWeight: 400, 
							lineHeight: '18px', 
							letterSpacing: '-0.1px', 
							color: page > 1 ? '#666' : '#ccc', 
							cursor: page > 1 ? 'pointer' : 'not-allowed', 
							padding: '0 16px 0 0'
						}}
					>
						← Previous
					</button>
					<div 
						style={{ 
							borderLeft: '1px solid #d9d9d9',
							margin: '0 8px',
							height: '20px'
						}}
					/>
					<button
						onClick={() => {
							const maxPage = Math.ceil(totalItems / pageSize);
							if (page < maxPage) {
								handlePageChange(page + 1);
							}
						}}
						disabled={page >= Math.ceil(totalItems / pageSize)}
						style={{ 
							background: 'none',
							border: 'none',
							fontFamily: 'Inter', 
							fontSize: '12px', 
							fontWeight: 400, 
							lineHeight: '18px', 
							letterSpacing: '-0.1px', 
							color: page < Math.ceil(totalItems / pageSize) ? '#666' : '#ccc', 
							cursor: page < Math.ceil(totalItems / pageSize) ? 'pointer' : 'not-allowed',
							padding: '0 0 0 8px'
						}}
					>
						Next →
					</button>
				</div>
			</div>

			<Modal
				title="Confirm Deletion"
				open={openDialog}
				onCancel={handleDialogClose}
				onOk={handleConfirmDelete}
				stopPropagation
				okText="Confirm"
				cancelText="Cancel"
			>
				<p>Are you sure you want to delete this role?</p>
			</Modal>

			
		</>
	);
};

export default DataTable;
